/**
 * GraphTool — Pestaña "Gráficos" embebida en MathWhiteboard.
 *
 * Tres herramientas (Sprint 1):
 *   1. Funciones (y = f(x))            → grafica con Recharts
 *   2. Plano cartesiano                → ingresa puntos y une (línea/polígono)
 *   3. Gráficos estadísticos           → barras / líneas / pastel con datos del estudiante
 *
 * Genera un JSON compacto (`buildGraphPayload`) que se envía al tutor en lugar
 * de imágenes, manteniendo el costo de tokens bajo.
 */
import React, { useMemo, useState } from "react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
  ScatterChart, Scatter, ReferenceLine, BarChart, Bar, PieChart, Pie, Cell, Legend
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LineChart as LineIcon, ScatterChart as ScatterIcon, BarChart3, Send, Trash2 } from "lucide-react";
import { evaluate } from "mathjs";
import { toast } from "sonner";

const SAMPLE_POINTS = 80;
const PIE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#84cc16"];

// ─── Helpers ────────────────────────────────────────────────────────────
const safeEval = (expr, x) => {
  try { return Number(evaluate(expr, { x })); } catch { return null; }
};

const buildFunctionData = (expr, xMin, xMax) => {
  if (!expr || xMin >= xMax) return { points: [], roots: [], yIntercept: null };
  const step = (xMax - xMin) / SAMPLE_POINTS;
  const points = [];
  const roots = [];
  let prevY = null;
  for (let i = 0; i <= SAMPLE_POINTS; i++) {
    const x = +(xMin + i * step).toFixed(3);
    const y = safeEval(expr, x);
    if (y == null || !Number.isFinite(y)) { prevY = null; continue; }
    points.push({ x, y: +y.toFixed(3) });
    // Approx roots between sign changes
    if (prevY != null && prevY * y < 0 && roots.length < 5) {
      roots.push({ x: +((x - step / 2)).toFixed(2) });
    }
    prevY = y;
  }
  const y0 = safeEval(expr, 0);
  const yIntercept = y0 != null && Number.isFinite(y0) ? { x: 0, y: +y0.toFixed(2) } : null;
  return { points, roots, yIntercept };
};

// ─── Function plotter ───────────────────────────────────────────────────
const FunctionPlotter = ({ expression, setExpression, xMin, xMax, setXMin, setXMax }) => {
  const { points, roots, yIntercept } = useMemo(
    () => buildFunctionData(expression, xMin, xMax), [expression, xMin, xMax]
  );
  return (
    <div className="space-y-3" data-testid="function-plotter">
      <div className="flex gap-2 items-center flex-wrap">
        <span className="text-sm text-gray-600 shrink-0">y =</span>
        <Input
          value={expression}
          onChange={(e) => setExpression(e.target.value)}
          placeholder="ej: 2x + 3, x^2 - 4, sin(x), sqrt(x)"
          className="flex-1 min-w-[200px]"
          data-testid="fn-input"
        />
        <span className="text-xs text-gray-500">x ∈</span>
        <Input type="number" value={xMin} onChange={(e) => setXMin(+e.target.value)} className="w-20" />
        <span className="text-gray-400">—</span>
        <Input type="number" value={xMax} onChange={(e) => setXMax(+e.target.value)} className="w-20" />
      </div>

      <div className="h-72 bg-gray-50/60 rounded-xl border border-gray-100 p-2">
        {points.length > 1 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="x" type="number" domain={[xMin, xMax]} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <ReferenceLine y={0} stroke="#9ca3af" />
              <ReferenceLine x={0} stroke="#9ca3af" />
              <Line type="monotone" dataKey="y" stroke="#3b82f6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-gray-400">
            Escribe una función válida para graficarla
          </div>
        )}
      </div>

      {(roots.length > 0 || yIntercept) && (
        <div className="text-xs text-gray-500 flex gap-4 flex-wrap">
          {yIntercept && <span>Corte con eje Y: ({yIntercept.x}, {yIntercept.y})</span>}
          {roots.length > 0 && <span>Raíces aproximadas: {roots.map(r => `x≈${r.x}`).join(", ")}</span>}
        </div>
      )}
    </div>
  );
};

// ─── Cartesian point plotter ────────────────────────────────────────────
const CartesianPlotter = ({ points, setPoints, joinMode, setJoinMode }) => {
  const [newX, setNewX] = useState("");
  const [newY, setNewY] = useState("");

  const add = () => {
    const xn = Number(newX); const yn = Number(newY);
    if (!Number.isFinite(xn) || !Number.isFinite(yn)) { toast.error("Coordenadas inválidas"); return; }
    setPoints([...points, { x: xn, y: yn }]);
    setNewX(""); setNewY("");
  };
  const remove = (i) => setPoints(points.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid="cartesian-plotter">
      <div className="flex gap-2 items-center flex-wrap">
        <Input type="number" placeholder="x" value={newX} onChange={(e) => setNewX(e.target.value)} className="w-24" data-testid="cart-x" />
        <Input type="number" placeholder="y" value={newY} onChange={(e) => setNewY(e.target.value)} className="w-24" data-testid="cart-y" />
        <Button onClick={add} size="sm" data-testid="cart-add">Agregar punto</Button>
        <div className="ml-2 flex gap-1 text-xs">
          {["puntos", "linea", "poligono"].map(m => (
            <button key={m} onClick={() => setJoinMode(m)}
                    className={`px-2 py-1 rounded ${joinMode === m ? "bg-purple-100 text-purple-700 font-medium" : "bg-gray-100 text-gray-500"}`}>
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="h-72 bg-gray-50/60 rounded-xl border border-gray-100 p-2">
        {points.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="x" type="number" tick={{ fontSize: 11 }} />
              <YAxis dataKey="y" type="number" tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ strokeDasharray: "3 3" }} />
              <ReferenceLine y={0} stroke="#9ca3af" />
              <ReferenceLine x={0} stroke="#9ca3af" />
              <Scatter data={points} fill="#8b5cf6" />
              {joinMode !== "puntos" && (
                <Line type="linear" dataKey="y"
                  data={joinMode === "poligono" && points.length > 2 ? [...points, points[0]] : points}
                  stroke="#8b5cf6" strokeWidth={2} dot={false} isAnimationActive={false} />
              )}
            </ScatterChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-gray-400">
            Agrega puntos al plano cartesiano
          </div>
        )}
      </div>

      {points.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {points.map((p, i) => (
            <span key={i} className="text-xs bg-purple-50 border border-purple-100 rounded px-2 py-1 flex items-center gap-1">
              ({p.x}, {p.y})
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Stats chart builder ─────────────────────────────────────────────────
const StatsBuilder = ({ chartType, setChartType, rows, setRows }) => {
  const [newCat, setNewCat] = useState("");
  const [newVal, setNewVal] = useState("");
  const add = () => {
    const v = Number(newVal);
    if (!newCat.trim() || !Number.isFinite(v)) { toast.error("Datos inválidos"); return; }
    setRows([...rows, { cat: newCat.trim(), val: v }]);
    setNewCat(""); setNewVal("");
  };
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid="stats-builder">
      <div className="flex gap-1 text-xs">
        {["barras", "linea", "pastel"].map(t => (
          <button key={t} onClick={() => setChartType(t)}
                  data-testid={`stats-type-${t}`}
                  className={`px-3 py-1.5 rounded ${chartType === t ? "bg-emerald-100 text-emerald-700 font-medium" : "bg-gray-100 text-gray-500"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Input placeholder="Categoría (ej: Lunes)" value={newCat} onChange={(e) => setNewCat(e.target.value)} className="flex-1 min-w-[140px]" data-testid="stats-cat" />
        <Input type="number" placeholder="Valor" value={newVal} onChange={(e) => setNewVal(e.target.value)} className="w-32" data-testid="stats-val" />
        <Button size="sm" onClick={add} data-testid="stats-add">Agregar</Button>
      </div>

      <div className="h-72 bg-gray-50/60 rounded-xl border border-gray-100 p-2">
        {rows.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "barras" ? (
              <BarChart data={rows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="cat" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="val" fill="#10b981" />
              </BarChart>
            ) : chartType === "linea" ? (
              <LineChart data={rows}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="cat" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="val" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            ) : (
              <PieChart>
                <Tooltip />
                <Legend />
                <Pie data={rows} dataKey="val" nameKey="cat" cx="50%" cy="50%" outerRadius={90} label>
                  {rows.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
              </PieChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-sm text-gray-400">
            Agrega categorías y valores para construir el gráfico
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {rows.map((r, i) => (
            <span key={i} className="text-xs bg-emerald-50 border border-emerald-100 rounded px-2 py-1 flex items-center gap-1">
              {r.cat}: {r.val}
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Build compact payload sent to the tutor ─────────────────────────────
const buildGraphPayload = (state) => {
  if (state.mode === "funcion") {
    const { points, roots, yIntercept } = buildFunctionData(state.expression, state.xMin, state.xMax);
    return {
      tipo: "funcion",
      expresion: state.expression,
      rango_x: [state.xMin, state.xMax],
      puntos_clave: [
        ...(yIntercept ? [{ x: 0, y: yIntercept.y, nota: "corte_y" }] : []),
        ...roots.map(r => ({ x: r.x, y: 0, nota: "raiz_aprox" })),
      ].slice(0, 8),
      muestra: points.length > 0 ? [points[0], points[Math.floor(points.length / 2)], points[points.length - 1]] : [],
    };
  }
  if (state.mode === "plano") {
    return {
      tipo: "plano_cartesiano",
      modo: state.joinMode,
      puntos: state.points.slice(0, 12),
    };
  }
  if (state.mode === "estadistico") {
    return {
      tipo: "estadistico",
      grafico: state.chartType,
      datos: state.rows.slice(0, 12),
    };
  }
  return null;
};

// ─── Main component ──────────────────────────────────────────────────────
export const GraphTool = ({ onSendToTutor, isSending }) => {
  const [mode, setMode] = useState("funcion");
  // function state
  const [expression, setExpression] = useState("2x + 3");
  const [xMin, setXMin] = useState(-10);
  const [xMax, setXMax] = useState(10);
  // cartesian state
  const [points, setPoints] = useState([]);
  const [joinMode, setJoinMode] = useState("puntos");
  // stats state
  const [chartType, setChartType] = useState("barras");
  const [rows, setRows] = useState([]);

  const reset = () => {
    if (mode === "funcion") { setExpression(""); }
    if (mode === "plano") { setPoints([]); }
    if (mode === "estadistico") { setRows([]); }
  };

  const sendToTutor = () => {
    const payload = buildGraphPayload({
      mode, expression, xMin, xMax, points, joinMode, chartType, rows,
    });
    if (!payload) return;
    if (payload.tipo === "funcion" && (!payload.expresion || payload.muestra.length === 0)) {
      toast.error("Ingresa una función válida primero");
      return;
    }
    if (payload.tipo === "plano_cartesiano" && payload.puntos.length === 0) {
      toast.error("Agrega al menos un punto");
      return;
    }
    if (payload.tipo === "estadistico" && payload.datos.length === 0) {
      toast.error("Agrega datos primero");
      return;
    }
    onSendToTutor && onSendToTutor(payload);
  };

  return (
    <div className="space-y-3" data-testid="graph-tool">
      <Tabs value={mode} onValueChange={setMode}>
        <TabsList className="grid grid-cols-3 w-full">
          <TabsTrigger value="funcion" data-testid="tab-funcion"><LineIcon className="w-4 h-4 mr-2" />Funciones</TabsTrigger>
          <TabsTrigger value="plano"   data-testid="tab-plano"><ScatterIcon className="w-4 h-4 mr-2" />Plano cartesiano</TabsTrigger>
          <TabsTrigger value="estadistico" data-testid="tab-estadistico"><BarChart3 className="w-4 h-4 mr-2" />Estadísticos</TabsTrigger>
        </TabsList>
        <TabsContent value="funcion" className="mt-3">
          <FunctionPlotter
            expression={expression} setExpression={setExpression}
            xMin={xMin} setXMin={setXMin} xMax={xMax} setXMax={setXMax}
          />
        </TabsContent>
        <TabsContent value="plano" className="mt-3">
          <CartesianPlotter points={points} setPoints={setPoints} joinMode={joinMode} setJoinMode={setJoinMode} />
        </TabsContent>
        <TabsContent value="estadistico" className="mt-3">
          <StatsBuilder chartType={chartType} setChartType={setChartType} rows={rows} setRows={setRows} />
        </TabsContent>
      </Tabs>

      <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
        <Button variant="ghost" size="sm" onClick={reset} className="gap-1" data-testid="graph-reset">
          <Trash2 className="w-4 h-4" /> Limpiar
        </Button>
        <Button size="sm" onClick={sendToTutor} disabled={isSending} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="graph-send">
          <Send className="w-4 h-4" /> {isSending ? "Enviando..." : "Enviar al tutor"}
        </Button>
      </div>
    </div>
  );
};

export default GraphTool;
